// Sprint 2026-Jun — VoltLedger integration Phase F
// Bulk Receipts: one customer receipt allocated across multiple invoices.
// Every allocation posts a STANDARD PrimeWork Payment Receipt (the untouched
// engine); the Receipt Voucher is only a grouping/tracking document.
// Includes Excel bulk upload: template → validated preview → all-or-nothing post.
import { Fragment, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatINR, previewNumber } from "@/lib/api";
import { PageHeader, Card } from "@/components/ui-helpers";
import { BranchSelect } from "@/components/BranchSelect";
import { SearchableSelect } from "@/components/SearchableSelect";
import { AttachmentsField } from "@/components/AttachmentsField";
import { BatchCnSpread } from "@/components/BatchCnSpread";
import { useBranch } from "@/contexts/BranchContext";
import { useAuth } from "@/contexts/AuthContext";
import { inputCls, Field } from "./Projects";
import { toast, Toaster } from "sonner";
import {
  ArrowDownToLine, CheckCircle2, ChevronDown, ChevronRight, Download,
  FileSpreadsheet, Loader2, Search, Upload, Wand2, X, XCircle,
} from "lucide-react";

const MODES = ["Bank", "Cash", "UPI", "Cheque"];
// VL-faithful retention heads (must match backend RETENTION_HEADS)
const RET_HEADS = [
  ["Retention PO Terms", "PO Terms"], ["IR Retention", "IR"],
  ["Safety Retention", "Safety"], ["BG Retention", "BG"],
  ["GST Retention", "GST"], ["Other Retention", "Other"],
];
const today = () => new Date().toISOString().slice(0, 10);
// CESS / LD / Round-off target-balance adjustments (client-approved Jun-2026).
// [ext field, label, allowNegative] — Round-off may be ± (positive reduces
// outstanding, negative increases it).
const ADJ_DEFS = [
  ["cess_amount", "CESS", false],
  ["ld_amount", "LD", false],
  ["rounding_deduction", "Round-off", true],
];

export default function BulkReceipts() {
  const [tab, setTab] = useState("manual");
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit") || "";
  return (
    <div data-testid="bulk-receipts-page">
      <Toaster richColors position="top-right" />
      <PageHeader
        eyebrow="Receivables"
        title={editId ? "Edit Receipt Voucher" : "Bulk Receipts"}
        subtitle={editId
          ? "Correct the entries below and save — the original receipts are reversed and the corrected ones are posted automatically under the same voucher number."
          : "Allocate one customer receipt across multiple invoices. Each allocation posts a standard Payment Receipt; the Receipt Voucher only groups them."}
      />
      {!editId && (
        <div className="flex gap-1 mb-4 border-b border-slate-200">
          {[["manual", "Manual Entry"], ["excel", "Excel Upload"]].map(([k, l]) => (
            <button key={k} data-testid={`bulk-receipts-tab-${k}`} onClick={() => setTab(k)}
              className={`px-4 py-2.5 text-xs uppercase tracking-wider font-bold border-b-2 -mb-px ${tab === k ? "border-orange-500 text-orange-600" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {l}
            </button>
          ))}
        </div>
      )}
      {editId || tab === "manual" ? <ManualEntry key={editId} editId={editId} /> : <ExcelUpload />}
    </div>
  );
}

function ManualEntry({ editId = "" }) {
  const navigate = useNavigate();
  const { active: activeBranch } = useBranch();
  const { hasPerm } = useAuth();
  const canViewRet = hasPerm("receivable_retentions", "view");
  const canHoldRet = hasPerm("receivable_retentions", "create");
  const canReleaseRet = hasPerm("receivable_retentions", "approve");
  const [clients, setClients] = useState([]);
  const [banks, setBanks] = useState([]);
  const [form, setForm] = useState({
    branch_id: activeBranch?.id || "", branch_name: activeBranch?.branch_name || activeBranch?.name || "",
    client_id: "", voucher_date: today(), mode: "Bank", bank_ledger_id: "",
    account: "", reference_no: "", narration: "", amount_received: "", attachments: [],
  });
  const [rvPreview, setRvPreview] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [allocs, setAllocs] = useState({}); // invoice_id -> {amount, tds}
  const [retOpen, setRetOpen] = useState({}); // invoice_id -> bool
  const [retTargets, setRetTargets] = useState({}); // invoice_id -> {head: "value"}
  const [cnData, setCnData] = useState({ rows: [], available_total: 0 });
  const [cnAlloc, setCnAlloc] = useState({}); // invoice_id -> {cn_id: "amount"}
  const [adjTargets, setAdjTargets] = useState({}); // invoice_id -> {field: "value"}
  const [adjReasons, setAdjReasons] = useState({}); // invoice_id -> reason
  const [invSearch, setInvSearch] = useState("");
  const [loadingInv, setLoadingInv] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editVoucher, setEditVoucher] = useState(null); // full edit mode (from register)
  const [batchCnMeta, setBatchCnMeta] = useState(null); // {policy, mode, entries} when batch CN spread applied

  useEffect(() => {
    // Only clients that actually have something outstanding to allocate.
    api.get("/receivables/clients-with-outstanding").then((r) => setClients(r.data || []));
  }, []);
  useEffect(() => {
    api.get("/bank-ledgers", { params: { active_only: true, ...(form.branch_id ? { branch_id: form.branch_id } : {}) } })
      .then((r) => setBanks(r.data || [])).catch(() => setBanks([]));
  }, [form.branch_id]);
  useEffect(() => {
    if (editId) return;
    previewNumber("receipt_voucher", form.voucher_date).then(setRvPreview);
  }, [form.voucher_date, editId]);

  // ─── Edit mode: load the voucher and prefill everything ───
  useEffect(() => {
    if (!editId) return;
    (async () => {
      try {
        const { data: v } = await api.get(`/receipt-vouchers/${editId}`);
        if (v.status !== "Posted") {
          toast.error("Only Posted vouchers can be edited");
          navigate("/receipt-voucher-register");
          return;
        }
        setEditVoucher(v);
        setForm((f) => ({
          ...f,
          branch_id: v.branch_id || "", branch_name: v.branch_name || "",
          client_id: v.client_id, voucher_date: v.voucher_date,
          mode: v.mode || "Bank", bank_ledger_id: v.bank_ledger_id || "",
          account: v.account || "", reference_no: v.reference_no || "",
          narration: v.narration || "", attachments: v.attachments || [],
        }));
        await loadInvoices(v.client_id, editId);
        const pre = {};
        (v.allocations || []).forEach((al) => {
          const tds = (Number(al.tds_deducted) || 0) + (Number(al.tds_informational) || 0);
          pre[al.invoice_id] = {
            amount: String(al.amount ?? ""),
            tds: tds ? String(tds) : "",
          };
        });
        setAllocs(pre);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to load voucher for edit");
        navigate("/receipt-voucher-register");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  const editBlocked = editVoucher
    && ((editVoucher.cn_allocations || []).length > 0
        || (editVoucher.adjustment_effects || []).length > 0);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function loadInvoices(clientId, addbackVid = editId) {
    if (!clientId) { setInvoices([]); setAllocs({}); return; }
    setLoadingInv(true);
    try {
      const r = await api.get("/receivables/outstanding-invoices", {
        params: { client_id: clientId, ...(addbackVid ? { addback_voucher_id: addbackVid } : {}) } });
      setInvoices(r.data || []);
      setAllocs({});
      setRetTargets({});
      setRetOpen({});
      setCnAlloc({});
      setBatchCnMeta(null);
      setAdjTargets({});
      setAdjReasons({});
      api.get("/credit-notes/allocatable", { params: { client_id: clientId } })
        .then((cr) => setCnData(cr.data || { rows: [], available_total: 0 }))
        .catch(() => setCnData({ rows: [], available_total: 0 }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load outstanding invoices");
    } finally { setLoadingInv(false); }
  }

  const setAlloc = (id, field, val) =>
    setAllocs((a) => {
      const cur = a[id] || {};
      const next = { amount: 0, tds: 0, ...cur, [field]: val };
      // Prefill suggested TDS the first time an amount is entered.
      if (field === "amount" && cur.tds === undefined && Number(val) > 0) {
        const inv = invoices.find((i) => i.id === id);
        if (inv && Number(inv.suggested_tds) > 0) next.tds = String(inv.suggested_tds);
      }
      return { ...a, [id]: next };
    });

  // TDS never consumes outstanding when the invoice already netted it
  // (single-deduction rule, Jul-2026) — it is recorded informationally.
  const deductingTds = (inv, tds) => (inv.tds_mode === "invoice_deducted" ? 0 : tds);

  // CN settles part of the invoice — entering/raising a CN allocation clamps
  // the cash allocation down so cash + TDS + CN never exceeds the invoice
  // balance (the Total Receipt visibly drops by the CN amount).
  const setCnAllocValue = (inv, cnId, val) => {
    setBatchCnMeta((m) => (m ? { ...m, mode: "manual_override" } : m));
    setCnAlloc((t) => ({ ...t, [inv.id]: { ...(t[inv.id] || {}), [cnId]: val } }));
    const cnSum = Object.entries({ ...(cnAlloc[inv.id] || {}), [cnId]: val })
      .reduce((s, [, raw]) => s + (Number(raw) || 0), 0);
    setAllocs((a) => {
      const cur = a[inv.id] || {};
      const cash = Number(cur.amount) || 0;
      if (cash <= 0) return a;
      const adjDelta = adjActionsFor(inv).reduce((s, x) => s + x.delta, 0);
      const maxCash = Math.round((inv.outstanding - adjDelta - cnSum
        - deductingTds(inv, Number(cur.tds) || 0) - retentionReservedFor(inv)) * 100) / 100;
      if (cash <= maxCash + 0.005) return a;
      return { ...a, [inv.id]: { ...cur, amount: String(Math.max(maxCash, 0)) } };
    });
  };

  function autoAllocate() {
    let pool = Number(form.amount_received) || 0;
    if (pool <= 0) { toast.error("Enter the Amount Received first, then auto-allocate FIFO"); return; }
    const next = {};
    for (const inv of invoices) {
      if (pool <= 0) break;
      const tds = Number(inv.suggested_tds) || 0;
      const cnHere = Object.values(cnAlloc[inv.id] || {}).reduce((s, raw) => s + (Number(raw) || 0), 0);
      const room = Math.max(Math.round((inv.outstanding - deductingTds(inv, tds) - cnHere
        - retentionReservedFor(inv)) * 100) / 100, 0);
      const take = Math.min(Math.round(Math.min(room, pool) * 100) / 100, pool);
      if (take > 0) { next[inv.id] = { amount: take, tds: tds || 0 }; pool = Math.round((pool - take) * 100) / 100; }
    }
    setAllocs(next);
    if (pool > 0) toast.warning(`${formatINR(pool)} could not be allocated — exceeds total outstanding`);
    else toast.success("Allocated FIFO across oldest invoices first (TDS auto-filled)");
  }

  const rows = invoices.map((inv) => {
    const a = allocs[inv.id] || {};
    return { inv, amount: Number(a.amount) || 0, tds: Number(a.tds) || 0 };
  });
  // Display-only filter — allocations on invoices hidden by the search are
  // still validated, summed and posted.
  const needle = invSearch.trim().toLowerCase();
  const visibleRows = needle
    ? rows.filter(({ inv }) =>
        `${inv.invoice_number} ${inv.project_name || ""} ${inv.invoice_date || ""} ${inv.payment_due_date || ""}`
          .toLowerCase().includes(needle))
    : rows;

  // VL target semantics: delta = target − currently pending for that head.
  function retActionsFor(inv) {
    const t = retTargets[inv.id] || {};
    const out = [];
    for (const [head] of RET_HEADS) {
      const raw = t[head];
      if (raw === undefined || raw === "") continue;
      const target = Number(raw) || 0;
      const current = Number((inv.pending_by_head || {})[head] || 0);
      const delta = Math.round((target - current) * 100) / 100;
      if (Math.abs(delta) < 0.005) continue;
      out.push({ head, target, current, delta });
    }
    return out;
  }
  const allRetActions = invoices.flatMap((inv) => retActionsFor(inv).map((a) => ({ ...a, inv })));
  const retHeld = Math.round(allRetActions.filter((a) => a.delta > 0).reduce((s, a) => s + a.delta, 0) * 100) / 100;
  const retReleased = Math.round(allRetActions.filter((a) => a.delta < 0).reduce((s, a) => s - a.delta, 0) * 100) / 100;
  const retPermBlocked = allRetActions.some((a) => (a.delta > 0 && !canHoldRet) || (a.delta < 0 && !canReleaseRet));

  // CESS/LD/Round-off target semantics (same pattern as retention targets).
  function adjActionsFor(inv) {
    const t = adjTargets[inv.id] || {};
    const out = [];
    for (const [field, label, allowNeg] of ADJ_DEFS) {
      const raw = t[field];
      if (raw === undefined || raw === "") continue;
      const target = Number(raw) || 0;
      if (!allowNeg && target < 0) continue;
      const current = Number(inv[field] || 0);
      const delta = Math.round((target - current) * 100) / 100;
      if (Math.abs(delta) < 0.005) continue;
      out.push({ field, label, target, current, delta });
    }
    return out;
  }
  function cnRowsFor(inv) {
    return Object.entries(cnAlloc[inv.id] || {})
      .map(([cn_id, raw]) => ({ cn_id, amount: Number(raw) || 0 }))
      .filter((c) => c.amount > 0);
  }
  const cnUsed = {};
  invoices.forEach((inv) => cnRowsFor(inv).forEach((c) => {
    cnUsed[c.cn_id] = Math.round(((cnUsed[c.cn_id] || 0) + c.amount) * 100) / 100;
  }));
  const cnOver = (cnData.rows || []).filter((c) => (cnUsed[c.cn_id] || 0) > c.available + 0.01);
  const totalCn = Math.round(Object.values(cnUsed).reduce((s, v) => s + v, 0) * 100) / 100;
  const allAdjActions = invoices.flatMap((inv) => adjActionsFor(inv).map((a) => ({ ...a, inv })));
  const adjNet = Math.round(allAdjActions.reduce((s, a) => s + a.delta, 0) * 100) / 100;
  const adjMissingReason = invoices.some((inv) => adjActionsFor(inv).length > 0 && !(adjReasons[inv.id] || "").trim());
  const effOutstanding = (inv) => {
    const adjDelta = adjActionsFor(inv).reduce((s, a) => s + a.delta, 0);
    return Math.round((inv.outstanding - adjDelta) * 100) / 100;
  };
  const cnSumFor = (inv) => cnRowsFor(inv).reduce((s, c) => s + c.amount, 0);
  const releasesFor = (inv) => Math.round(retActionsFor(inv)
    .filter((a) => a.delta < 0).reduce((s, a) => s - a.delta, 0) * 100) / 100;
  // Fix C (2026-Jul, user-approved): unreleased retention stays a recoverable
  // receivable — cash/TDS/CN can never consume it. Only the portion released
  // in THIS receipt becomes collectible.
  const retentionReservedFor = (inv) =>
    Math.max(Math.round(((Number(inv.pending_retention) || 0) - releasesFor(inv)) * 100) / 100, 0);
  const availableNowFor = (inv) =>
    Math.round((effOutstanding(inv) - retentionReservedFor(inv)) * 100) / 100;

  // Mirror of the backend retention-room rule: new holds must fit inside the
  // un-collected non-retention balance AFTER cash, TDS and CN allocations.
  const newHoldsFor = (inv) => Math.round(retActionsFor(inv)
    .filter((a) => a.delta > 0).reduce((s, a) => s + a.delta, 0) * 100) / 100;
  const retRoomFor = (inv) => {
    const a = allocs[inv.id] || {};
    return Math.round((effOutstanding(inv) - cnSumFor(inv)
      - (Number(a.amount) || 0) - deductingTds(inv, Number(a.tds) || 0)
      - retentionReservedFor(inv)) * 100) / 100;
  };

  const active = rows.filter((r) => r.amount > 0 || r.tds > 0 || retActionsFor(r.inv).length > 0
    || adjActionsFor(r.inv).length > 0 || cnRowsFor(r.inv).length > 0);
  const totalCash = active.reduce((s, r) => s + r.amount, 0);
  const totalTdsRecorded = active.reduce((s, r) => s + r.tds, 0);
  const totalAlloc = active.reduce((s, r) => s + r.amount + deductingTds(r.inv, r.tds), 0);
  const totalReceipt = Math.round((totalCash + retReleased) * 100) / 100;
  const overRows = rows.filter((r) => r.amount + deductingTds(r.inv, r.tds) + cnSumFor(r.inv) > effOutstanding(r.inv) + 0.01);
  // Fix C: within outstanding but eating into unreleased retention.
  const retConsumeRows = rows.filter((r) => {
    const total = r.amount + deductingTds(r.inv, r.tds) + cnSumFor(r.inv);
    return total <= effOutstanding(r.inv) + 0.01 && total > availableNowFor(r.inv) + 0.01;
  });
  const retOverRows = rows.filter((r) => newHoldsFor(r.inv) > retRoomFor(r.inv) + 0.01);

  async function save() {
    if (!form.client_id) return toast.error("Select a client");
    if (!form.voucher_date) return toast.error("Receipt date is required");
    if (!active.length) return toast.error("Allocate at least one invoice (payment, retention, adjustment or credit note)");
    if (overRows.length) return toast.error(`${overRows.length} allocation(s) exceed the invoice outstanding`);
    if (retConsumeRows.length) {
      const r = retConsumeRows[0];
      return toast.error(`${r.inv.invoice_number}: cash/Credit Note allocation cannot consume unreleased retention ${formatINR(retentionReservedFor(r.inv))} — release the applicable retention first, or reduce the allocation`);
    }
    if (retOverRows.length) {
      const r = retOverRows[0];
      return toast.error(`${r.inv.invoice_number}: retention holds ${formatINR(newHoldsFor(r.inv))} exceed the available balance ${formatINR(Math.max(retRoomFor(r.inv), 0))} after cash/TDS/CN — lower a retention target or the CN amount`);
    }
    if (cnOver.length) return toast.error("A Credit Note allocation exceeds its available balance");
    if (adjMissingReason) return toast.error("Enter a reason for every invoice with CESS/LD/Round-off changes");
    if (retHeld > 0 && !canHoldRet) return toast.error("You lack permission to hold new retention (receivable_retentions.create)");
    if (retReleased > 0 && !canReleaseRet) return toast.error("You lack permission to release retention (receivable_retentions.approve)");
    if (editId && editBlocked) return toast.error("This voucher carries CN allocations or adjustments — reverse it and create a new one");
    setSaving(true);
    try {
      const bank = banks.find((b) => b.id === form.bank_ledger_id);
      const client = clients.find((c) => c.id === form.client_id);
      const account = form.mode === "Cash" ? "Cash" : (bank ? `${bank.bank_name} — ${bank.account_number}` : form.account);
      if (editId) {
        // Full edit: backend reverses the original receipts and reposts the
        // corrected ones under the SAME voucher number.
        const { data } = await api.put(`/receipt-vouchers/${editId}`, {
          voucher_date: form.voucher_date,
          mode: form.mode, bank_ledger_id: form.bank_ledger_id,
          account, reference_no: form.reference_no, narration: form.narration,
          attachments: form.attachments,
          allocations: active
            .filter((r) => r.amount > 0 || r.tds > 0)
            .map((r) => ({ invoice_id: r.inv.id, amount: r.amount, tds_deducted: r.tds })),
        });
        toast.success(`${data.voucher_number} updated — original receipts reversed, corrected receipts posted`);
        navigate("/receipt-voucher-register");
        return;
      }
      const { data } = await api.post("/receipt-vouchers", {
        client_id: form.client_id, client_name: client?.name || "",
        voucher_date: form.voucher_date,
        branch_id: form.branch_id, branch_name: form.branch_name,
        mode: form.mode, bank_ledger_id: form.bank_ledger_id,
        account,
        reference_no: form.reference_no, narration: form.narration,
        attachments: form.attachments,
        ...(batchCnMeta && totalCn > 0 ? { batch_cn: batchCnMeta } : {}),
        allocations: active.map((r) => {
          const acts = retActionsFor(r.inv);
          const adjs = adjActionsFor(r.inv);
          const cns = cnRowsFor(r.inv);
          const alloc = { invoice_id: r.inv.id, amount: r.amount, tds_deducted: r.tds };
          if (acts.length) alloc.retention_targets = Object.fromEntries(acts.map((a) => [a.head, a.target]));
          if (adjs.length) {
            alloc.adjustment_targets = Object.fromEntries(adjs.map((a) => [a.field, a.target]));
            alloc.adjustment_reason = (adjReasons[r.inv.id] || "").trim();
          }
          if (cns.length) alloc.cn_allocations = cns;
          return alloc;
        }),
      });
      const retMsg = (data.retention_held_total > 0 || data.retention_released_total > 0)
        ? ` · Retention held ${formatINR(data.retention_held_total)} / released ${formatINR(data.retention_released_total)}`
        : "";
      const cnMsg = data.cn_allocation_total > 0 ? ` · CN allocated ${formatINR(data.cn_allocation_total)}` : "";
      const adjMsg = data.adjustments_net_delta
        ? ` · Adjustments Δ ${data.adjustments_net_delta > 0 ? "−" : "+"}${formatINR(Math.abs(data.adjustments_net_delta))}`
        : "";
      toast.success(`Receipt Voucher ${data.voucher_number} posted — ${data.allocation_count} payment receipt(s)${retMsg}${cnMsg}${adjMsg}`);
      setForm((f) => ({ ...f, reference_no: "", narration: "", amount_received: "", attachments: [] }));
      setRetTargets({});
      setRetOpen({});
      setCnAlloc({});
      setAdjTargets({});
      setAdjReasons({});
      loadInvoices(form.client_id, "");
      previewNumber("receipt_voucher", form.voucher_date).then(setRvPreview);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to post bulk receipt");
    } finally { setSaving(false); }
  }

  return (
    <div data-testid="bulk-receipt-manual">
      {editId && editVoucher && (
        <div className={`mb-4 border p-3 text-xs flex items-start gap-2 ${editBlocked
          ? "border-rose-300 bg-rose-50 text-rose-800"
          : "border-sky-300 bg-sky-50 text-sky-900"}`}
          data-testid="bulk-receipt-edit-banner">
          <div className="flex-1">
            <b>Editing {editVoucher.voucher_number}</b> ({editVoucher.client_name}).{" "}
            {editBlocked
              ? "This voucher carries Credit Note allocations or CESS/LD/Round-off adjustments and cannot be re-allocated here — reverse it from the register and create a new one (audit-safe)."
              : "Saving reverses the original receipts and posts the corrected ones automatically — the debtor outstanding updates and the voucher number stays the same. Retention / CN / adjustments are locked in edit mode."}
          </div>
          <button data-testid="bulk-receipt-edit-cancel" onClick={() => navigate("/receipt-voucher-register")}
            className="border border-slate-300 bg-white text-slate-700 hover:bg-slate-100 px-3 py-1.5 text-[11px] uppercase tracking-wider font-bold whitespace-nowrap">
            Cancel Edit
          </button>
        </div>
      )}
      <Card className="p-4 mb-4">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">Receipt Details</div>
          <div data-testid="rv-number-preview" className="text-xs font-bold text-slate-700 border border-slate-200 bg-slate-50 px-3 py-1.5">
            {editId
              ? <>Voucher No: <span className="text-orange-600">{editVoucher?.voucher_number || "…"}</span></>
              : <>Voucher No (auto): <span className="text-orange-600">{rvPreview || "—"}</span></>}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Branch">
            <BranchSelect value={form.branch_id} required={false}
              onChange={(id, name) => setForm((f) => ({ ...f, branch_id: id, branch_name: name }))}
              testid="bulk-receipt-branch" />
          </Field>
          <Field label="Client *">
            {editId ? (
              <input data-testid="bulk-receipt-client-locked" className={`${inputCls} bg-slate-50 text-slate-500`}
                value={editVoucher?.client_name || ""} disabled />
            ) : (
            <SearchableSelect
              testid="bulk-receipt-client"
              value={form.client_id}
              onChange={(v) => { set("client_id", v); loadInvoices(v, ""); }}
              items={clients}
              placeholder="Select client…"
              searchPlaceholder="Search clients…"
            />
            )}
          </Field>
          <Field label="Receipt Date *">
            <input data-testid="bulk-receipt-date" type="date" className={inputCls}
              value={form.voucher_date} onChange={(e) => set("voucher_date", e.target.value)} />
          </Field>
          <Field label="Mode">
            <select data-testid="bulk-receipt-mode" className={inputCls} value={form.mode}
              onChange={(e) => set("mode", e.target.value)}>
              {MODES.map((m) => <option key={m}>{m}</option>)}
            </select>
          </Field>
          {form.mode !== "Cash" && (
            <Field label="Bank Account">
              <select data-testid="bulk-receipt-bank" className={inputCls} value={form.bank_ledger_id}
                onChange={(e) => set("bank_ledger_id", e.target.value)}>
                <option value="">Select bank…</option>
                {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name} — {b.account_number}</option>)}
              </select>
            </Field>
          )}
          <Field label="Reference No">
            <input data-testid="bulk-receipt-reference" className={inputCls} value={form.reference_no}
              onChange={(e) => set("reference_no", e.target.value)} placeholder="UTR / Cheque no…" />
          </Field>
          <Field label="Narration" full>
            <input data-testid="bulk-receipt-narration" className={inputCls} value={form.narration}
              onChange={(e) => set("narration", e.target.value)} placeholder="Optional narration for all receipts…" />
          </Field>
          <div className="col-span-2 md:col-span-4">
            <AttachmentsField label="Attachments (bank advice / UTR proof)" value={form.attachments}
              onChange={(v) => set("attachments", v)} />
          </div>
        </div>
      </Card>

      {!editId && form.client_id && (
        <BatchCnSpread
          clientId={form.client_id}
          cnRows={cnData.rows || []}
          invoices={invoices}
          allocs={allocs}
          adjDeltaFor={(inv) => adjActionsFor(inv).reduce((s, x) => s + x.delta, 0)}
          retentionTargetsFor={(inv) => retTargets[inv.id] || {}}
          applied={batchCnMeta}
          onApply={(mapping, meta) => { setCnAlloc(mapping); setBatchCnMeta(meta); }}
          onClear={() => { setCnAlloc({}); setBatchCnMeta(null); }}
        />
      )}

      <Card>
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500">
            Outstanding Invoices {invoices.length ? `(${invoices.length})` : ""}
            {needle && (
              <span className="ml-2 normal-case tracking-normal font-semibold text-slate-400" data-testid="bulk-receipt-search-count">
                showing {visibleRows.length} of {rows.length}
              </span>
            )}
            {needle && active.some((r) => !visibleRows.includes(r)) && (
              <span className="ml-2 normal-case tracking-normal font-semibold text-orange-600" data-testid="bulk-receipt-hidden-allocations">
                (some allocated invoices are hidden by the search — they will still be posted)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-2.5 text-slate-400" />
              <input data-testid="bulk-receipt-invoice-search"
                className="border border-slate-300 pl-8 pr-7 py-2 text-xs w-56 focus:outline-none focus:border-orange-500"
                placeholder="Search invoice #, project…" value={invSearch}
                onChange={(e) => setInvSearch(e.target.value)} />
              {invSearch && (
                <button data-testid="bulk-receipt-invoice-search-clear" onClick={() => setInvSearch("")}
                  className="absolute right-1.5 top-1.5 text-slate-400 hover:text-slate-600 p-0.5">
                  <X size={12} />
                </button>
              )}
            </div>
            <input data-testid="bulk-receipt-amount-received" type="number" min="0" step="0.01"
              className="border border-slate-300 px-3 py-2 text-sm w-44 focus:outline-none focus:border-orange-500"
              placeholder="Amount received ₹" value={form.amount_received}
              onChange={(e) => set("amount_received", e.target.value)} />
            <button data-testid="bulk-receipt-auto-allocate" onClick={autoAllocate}
              className="flex items-center gap-1.5 border border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100 px-3 py-2 text-xs uppercase tracking-wider font-bold">
              <Wand2 size={13} /> Auto Allocate (FIFO)
            </button>
          </div>
        </div>
        <div className="overflow-x-auto max-h-[55vh] overflow-y-auto" data-testid="bulk-receipt-invoice-scrollbox">
          <table className="w-full text-xs" data-testid="bulk-receipt-invoice-table">
            <thead className="sticky top-0 z-10">
              <tr className="bg-slate-100 text-slate-600">
                {["Invoice", "Date", "Due Date", "Project", "Net Payable", "Received", "Outstanding", "Allocate ₹", "TDS ₹", "Adjust"].map((h) => (
                  <th key={h} className="px-3 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap bg-slate-100">{h}</th>))}
              </tr>
            </thead>
            <tbody>
              {loadingInv && <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">Loading…</td></tr>}
              {!loadingInv && !invoices.length && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400">
                  {form.client_id ? "No outstanding invoices for this client." : "Select a client to see outstanding invoices."}
                </td></tr>
              )}
              {!loadingInv && invoices.length > 0 && visibleRows.length === 0 && (
                <tr><td colSpan={10} className="px-3 py-8 text-center text-slate-400" data-testid="bulk-receipt-search-empty">
                  No invoices match “{invSearch}”.
                </td></tr>
              )}
              {!loadingInv && visibleRows.map(({ inv, amount, tds }) => {
                const over = amount + deductingTds(inv, tds) + cnSumFor(inv) > availableNowFor(inv) + 0.01;
                const acts = retActionsFor(inv);
                const adjs = adjActionsFor(inv);
                const cns = cnRowsFor(inv);
                const touched = amount + tds > 0 || acts.length > 0 || adjs.length > 0 || cns.length > 0;
                return (
                  <Fragment key={inv.id}>
                  <tr className={`border-b border-slate-50 ${touched ? "bg-orange-50/40" : ""}`}>
                    <td className="px-3 py-2 font-semibold whitespace-nowrap">{inv.invoice_number}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{(inv.invoice_date || "").slice(0, 10)}</td>
                    <td className="px-3 py-2 whitespace-nowrap">{inv.payment_due_date || <span className="text-rose-500 font-semibold">Missing</span>}</td>
                    <td className="px-3 py-2 whitespace-nowrap max-w-[140px] truncate" title={inv.project}>{inv.project}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(inv.net_payable)}</td>
                    <td className="px-3 py-2 text-right tabular-nums">{formatINR(inv.total_paid)}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-bold">{formatINR(inv.outstanding)}</td>
                    <td className="px-3 py-2">
                      <input data-testid={`alloc-amount-${inv.invoice_number}`} type="number" min="0" step="0.01"
                        className={`w-28 border px-2 py-1.5 text-right text-xs focus:outline-none ${over ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-orange-500"}`}
                        value={allocs[inv.id]?.amount ?? ""} onChange={(e) => setAlloc(inv.id, "amount", e.target.value)} />
                    </td>
                    <td className="px-3 py-2">
                      <input data-testid={`alloc-tds-${inv.invoice_number}`} type="number" min="0" step="0.01"
                        className={`w-20 border px-2 py-1.5 text-right text-xs focus:outline-none ${over ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-orange-500"}`}
                        value={allocs[inv.id]?.tds ?? ""} onChange={(e) => setAlloc(inv.id, "tds", e.target.value)} />
                      {inv.tds_mode === "invoice_deducted" && (
                        <div className="text-[9px] text-sky-700 font-semibold mt-0.5 whitespace-nowrap" data-testid={`tds-badge-${inv.invoice_number}`}>
                          Invoice TDS {formatINR(inv.tds_amount)}{Number(inv.si_tds_rate) > 0 ? ` @ ${inv.si_tds_rate}%` : ""} — recorded once, no double deduction
                        </div>
                      )}
                      {inv.tds_mode === "receipt_level" && Number(inv.suggested_tds) > 0 && (
                        <div className="text-[9px] text-slate-500 mt-0.5 whitespace-nowrap" data-testid={`tds-suggested-${inv.invoice_number}`}>
                          Suggested {formatINR(inv.suggested_tds)}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button data-testid={`ret-toggle-${inv.invoice_number}`}
                        disabled={!!editId}
                        title={editId ? "Retention / CN / adjustments are locked in voucher edit — reverse & recreate to change them" : ""}
                        onClick={() => setRetOpen((o) => ({ ...o, [inv.id]: !o[inv.id] }))}
                        className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-1 border disabled:opacity-50 disabled:cursor-not-allowed ${(acts.length + adjs.length + cns.length) ? "border-orange-400 bg-orange-100 text-orange-800" : Number(inv.pending_retention) > 0 ? "border-sky-200 bg-sky-50 text-sky-700" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                        {retOpen[inv.id] ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                        {Number(inv.pending_retention) > 0 ? `Held ${formatINR(inv.pending_retention)}` : "Adjust"}
                        {(acts.length + adjs.length + cns.length) > 0 && <span className="ml-1">({acts.length + adjs.length + cns.length})</span>}
                      </button>
                    </td>
                  </tr>
                  {retOpen[inv.id] && (
                    <tr className="border-b border-slate-100 bg-slate-50/80">
                      <td colSpan={10} className="px-4 py-3" data-testid={`ret-panel-${inv.invoice_number}`}>
                        {canViewRet && (
                        <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
                          Retention Targets — set each head to the balance that should remain held (raise = hold more, lower = release)
                        </div>
                        <div className={`text-[10px] font-bold mb-2 ${newHoldsFor(inv) > retRoomFor(inv) + 0.01 ? "text-rose-600" : "text-slate-500"}`}
                          data-testid={`ret-room-${inv.invoice_number}`}>
                          {newHoldsFor(inv) > retRoomFor(inv) + 0.01
                            ? <>Holds {formatINR(newHoldsFor(inv))} exceed the available balance {formatINR(Math.max(retRoomFor(inv), 0))} (after cash / TDS / CN {formatINR(cnSumFor(inv))}) — lower a retention target or the CN amount</>
                            : <>Available to hold: {formatINR(Math.max(retRoomFor(inv), 0))}{cnSumFor(inv) > 0 ? <> (after CN {formatINR(cnSumFor(inv))})</> : null}</>}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                          {RET_HEADS.map(([head, label]) => {
                            const current = Number((inv.pending_by_head || {})[head] || 0);
                            const raw = (retTargets[inv.id] || {})[head];
                            const value = raw !== undefined ? raw : (current ? String(current) : "");
                            const target = raw === undefined || raw === "" ? current : (Number(raw) || 0);
                            const delta = Math.round((target - current) * 100) / 100;
                            const needPerm = delta > 0 ? canHoldRet : delta < 0 ? canReleaseRet : true;
                            return (
                              <div key={head} className="bg-white border border-slate-200 p-2">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
                                  <span className="text-[10px] text-slate-400">Held {formatINR(current)}</span>
                                </div>
                                <input data-testid={`ret-target-${inv.invoice_number}-${label.replace(/\s/g, "")}`}
                                  type="number" min="0" step="0.01" placeholder={String(current)}
                                  disabled={!canHoldRet && !canReleaseRet}
                                  className="w-full border border-slate-300 px-2 py-1.5 text-right text-xs focus:outline-none focus:border-orange-500 disabled:bg-slate-50 disabled:text-slate-400"
                                  value={value}
                                  onChange={(e) => setRetTargets((t) => ({
                                    ...t, [inv.id]: { ...(t[inv.id] || {}), [head]: e.target.value } }))} />
                                <div className="mt-1 text-[10px] font-bold" data-testid={`ret-action-${inv.invoice_number}-${label.replace(/\s/g, "")}`}>
                                  {!needPerm
                                    ? <span className="text-rose-600">No permission to {delta > 0 ? "hold" : "release"}</span>
                                    : delta > 0
                                      ? <span className="text-emerald-700">Hold {formatINR(delta)}</span>
                                      : delta < 0
                                        ? <span className="text-amber-700">Release {formatINR(-delta)}</span>
                                        : <span className="text-slate-400">No change</span>}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        </div>
                        )}

                        {/* CESS / LD / Round-off adjustments */}
                        <div className={canViewRet ? "mt-4" : ""} data-testid={`adj-panel-${inv.invoice_number}`}>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
                            CESS / LD / Round-off — enter the new TOTAL for each (target balance; Round-off may be negative)
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {ADJ_DEFS.map(([field, label, allowNeg]) => {
                              const current = Number(inv[field] || 0);
                              const raw = (adjTargets[inv.id] || {})[field];
                              const value = raw !== undefined ? raw : (current ? String(current) : "");
                              const target = raw === undefined || raw === "" ? current : (Number(raw) || 0);
                              const delta = Math.round((target - current) * 100) / 100;
                              const key = label.replace(/[^A-Za-z]/g, "");
                              return (
                                <div key={field} className="bg-white border border-slate-200 p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-[10px] font-bold uppercase tracking-wider text-slate-600">{label}</span>
                                    <span className="text-[10px] text-slate-400">Now {formatINR(current)}</span>
                                  </div>
                                  <input data-testid={`adj-target-${inv.invoice_number}-${key}`}
                                    type="number" step="0.01" {...(allowNeg ? {} : { min: 0 })} placeholder={String(current)}
                                    className="w-full border border-slate-300 px-2 py-1.5 text-right text-xs focus:outline-none focus:border-orange-500"
                                    value={value}
                                    onChange={(e) => setAdjTargets((t) => ({
                                      ...t, [inv.id]: { ...(t[inv.id] || {}), [field]: e.target.value } }))} />
                                  <div className="mt-1 text-[10px] font-bold" data-testid={`adj-action-${inv.invoice_number}-${key}`}>
                                    {delta > 0
                                      ? <span className="text-emerald-700">{field === "rounding_deduction" ? "Round-off" : `Additional ${label}`} {formatINR(delta)} — reduces outstanding</span>
                                      : delta < 0
                                        ? <span className="text-rose-700">{label} reduced by {formatINR(-delta)} — increases outstanding</span>
                                        : <span className="text-slate-400">No change</span>}
                                  </div>
                                </div>
                              );
                            })}
                            <div className="bg-white border border-slate-200 p-2">
                              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">Adjustment Reason *</div>
                              <input data-testid={`adj-reason-${inv.invoice_number}`} placeholder="Required if any change…"
                                className={`w-full border px-2 py-1.5 text-xs focus:outline-none ${adjs.length && !(adjReasons[inv.id] || "").trim() ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-orange-500"}`}
                                value={adjReasons[inv.id] || ""}
                                onChange={(e) => setAdjReasons((t) => ({ ...t, [inv.id]: e.target.value }))} />
                              <div className="mt-1 text-[10px] text-slate-400">Recorded in the voucher & audit trail</div>
                            </div>
                          </div>
                        </div>

                        {/* Credit Note allocation — CNs with zero available balance are hidden (Sep-2026 user request) */}
                        {(() => {
                          const visibleCns = (cnData.rows || []).filter((cn) =>
                            (cn.available || 0) > 0.009 || Number((cnAlloc[inv.id] || {})[cn.cn_id] || 0) > 0);
                          return (
                        <div className="mt-4" data-testid={`cn-panel-${inv.invoice_number}`}>
                          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-2">
                            Credit Note Allocation — approved CNs of this client (reduces outstanding directly; no cash / bank entry)
                          </div>
                          {!visibleCns.length && (
                            <div className="text-xs text-slate-400">No approved Credit Notes with available balance for this client.</div>
                          )}
                          {visibleCns.length > 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                              {visibleCns.map((cn) => {
                                const used = cnUsed[cn.cn_id] || 0;
                                const overCn = used > cn.available + 0.01;
                                return (
                                  <div key={cn.cn_id} className={`bg-white border p-2 ${cn.legacy_full_allocation ? "border-slate-200 opacity-60" : overCn ? "border-rose-300" : "border-slate-200"}`}>
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="text-[10px] font-bold text-violet-700">{cn.note_number}</span>
                                      <span className="text-[10px] text-slate-400">{cn.note_date}</span>
                                    </div>
                                    <div className="text-[10px] text-slate-500 mb-1">
                                      Total {formatINR(cn.total_amount)} · Used {formatINR(Math.round((cn.allocated_total + used) * 100) / 100)} ·{" "}
                                      {cn.legacy_full_allocation
                                        ? <span className="font-bold text-slate-500">Legacy full allocation{cn.legacy_invoice_number ? ` → ${cn.legacy_invoice_number}` : ""}</span>
                                        : <span className={`font-bold ${overCn ? "text-rose-600" : "text-emerald-700"}`}>Available {formatINR(Math.max(Math.round((cn.available - used) * 100) / 100, 0))}</span>}
                                    </div>
                                    <input data-testid={`cn-alloc-${inv.invoice_number}-${cn.note_number}`}
                                      type="number" min="0" step="0.01" placeholder="Allocate ₹ to this invoice"
                                      disabled={cn.legacy_full_allocation}
                                      className={`w-full border px-2 py-1.5 text-right text-xs focus:outline-none disabled:bg-slate-50 disabled:text-slate-400 ${overCn ? "border-rose-400 bg-rose-50" : "border-slate-300 focus:border-orange-500"}`}
                                      value={(cnAlloc[inv.id] || {})[cn.cn_id] ?? ""}
                                      onChange={(e) => setCnAllocValue(inv, cn.cn_id, e.target.value)} />
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 flex-wrap text-xs" data-testid="bulk-receipt-summary-bar">
            <span className="border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid="summary-payment-received">
              Payment Received <span className="font-bold text-slate-900">{formatINR(totalCash)}</span>
            </span>
            <span className="border border-slate-200 bg-slate-50 px-2.5 py-1.5" data-testid="summary-tds">
              TDS <span className="font-bold text-slate-900">{formatINR(totalTdsRecorded)}</span>
            </span>
            <span className="border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-emerald-800" data-testid="summary-retention-held">
              Retention Held <span className="font-bold">{formatINR(retHeld)}</span>
            </span>
            <span className="border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-amber-800" data-testid="summary-retention-released">
              Retention Released <span className="font-bold">{formatINR(retReleased)}</span>
            </span>
            <span className="border border-violet-200 bg-violet-50 px-2.5 py-1.5 text-violet-800" data-testid="summary-cn-allocated">
              CN Allocated <span className="font-bold">{formatINR(totalCn)}</span>
            </span>
            {adjNet !== 0 && (
              <span className="border border-sky-200 bg-sky-50 px-2.5 py-1.5 text-sky-800" data-testid="summary-adjustments">
                Adjustments <span className="font-bold">{adjNet > 0 ? "−" : "+"}{formatINR(Math.abs(adjNet))} outstanding</span>
              </span>
            )}
            <span className="border border-orange-300 bg-orange-50 px-2.5 py-1.5 text-orange-800" data-testid="summary-total-receipt">
              Total Receipt <span className="font-bold">{formatINR(totalReceipt)}</span>
            </span>
            <span className="text-slate-500" data-testid="bulk-receipt-totals">
              <span className="font-bold">{active.length}</span> invoice(s)
              {Number(form.amount_received) > 0 && (
                <span className={Math.abs(totalAlloc - Number(form.amount_received)) > 0.01 ? " text-amber-600 font-semibold" : " text-emerald-600 font-semibold"}>
                  {" "}· received {formatINR(Number(form.amount_received))}
                </span>
              )}
              {overRows.length > 0 && <span className="text-rose-600 font-bold"> · {overRows.length} over-allocated!</span>}
              {retOverRows.length > 0 && <span className="text-rose-600 font-bold" data-testid="summary-retention-over"> · retention exceeds balance after CN!</span>}
              {retPermBlocked && <span className="text-rose-600 font-bold"> · retention permission missing!</span>}
              {cnOver.length > 0 && <span className="text-rose-600 font-bold"> · CN over-allocated!</span>}
              {adjMissingReason && <span className="text-rose-600 font-bold"> · adjustment reason missing!</span>}
            </span>
          </div>
          <button data-testid="bulk-receipt-save" onClick={save}
            disabled={saving || !active.length || !!overRows.length || !!retOverRows.length || retPermBlocked || !!cnOver.length || adjMissingReason || (editId && editBlocked)}
            className="flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-50 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <ArrowDownToLine size={14} />}
            {editId ? "Update Voucher" : "Post Bulk Receipt"}
          </button>
        </div>
      </Card>
    </div>
  );
}

function ExcelUpload() {
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [result, setResult] = useState(null);

  async function downloadTemplate() {
    try {
      const r = await api.get("/receipt-vouchers/upload-template", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "bulk_receipts_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download template"); }
  }

  async function upload(file) {
    if (!file) return;
    setUploading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipt-vouchers/upload-preview", fd);
      setPreview(data);
      setFileName(file.name);
      if (data.error_count > 0) toast.warning(`${data.error_count} row(s) have errors — fix and re-upload`);
      else toast.success(`All ${data.valid_count} row(s) valid — review and confirm`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
      setPreview(null);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function commit() {
    if (!preview || preview.error_count > 0 || !preview.valid_count) return;
    setCommitting(true);
    try {
      const { data } = await api.post("/receipt-vouchers/upload-commit", {
        rows: preview.rows.map((r) => ({
          row_no: r.row_no, client_name: r.client_name, invoice_number: r.invoice_number,
          receipt_date: r.receipt_date, amount: r.amount, tds_deducted: r.tds_deducted,
          tds_override_reason: r.tds_override_reason || "",
          mode: r.mode, reference_no: r.reference_no, narration: r.narration,
          retention_holds: r.retention_holds || {},
          cess_deduction: r.cess_deduction || 0,
          ld_deduction: r.ld_deduction || 0,
          roundoff_delta: r.roundoff_delta || 0,
          adjustment_reason: r.adjustment_reason || "",
        })),
      });
      setResult(data);
      setPreview(null);
      toast.success(`${data.voucher_count} Receipt Voucher(s) posted`);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : d?.message || "Commit failed — nothing was posted");
    } finally { setCommitting(false); }
  }

  return (
    <div data-testid="bulk-receipt-excel">
      <Card className="p-4 mb-4">
        <div className="flex items-center gap-3 flex-wrap">
          <button data-testid="excel-download-template" onClick={downloadTemplate}
            className="flex items-center gap-2 border border-slate-300 text-slate-700 hover:bg-slate-50 px-4 py-2.5 text-xs uppercase tracking-wider font-bold">
            <Download size={14} /> Download Template
          </button>
          <label className="flex items-center gap-2 bg-[#F97316] hover:bg-[#EA580C] text-white px-4 py-2.5 text-xs uppercase tracking-wider font-bold cursor-pointer">
            {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {uploading ? "Validating…" : "Upload .xlsx"}
            <input data-testid="excel-upload-input" ref={fileRef} type="file" accept=".xlsx" className="hidden"
              onChange={(e) => upload(e.target.files?.[0])} />
          </label>
          {fileName && <span className="text-xs text-slate-500 flex items-center gap-1.5"><FileSpreadsheet size={13} /> {fileName}</span>}
        </div>
        <div className="text-[11px] text-slate-500 mt-2">
          Rows with the same Client + Date + Mode + Reference No group into one Receipt Voucher.
          The template includes per-head <b>Retention Hold</b> columns and <b>CESS / LD / Round-off</b> deduction
          columns (additive amounts; Adjustment Reason is mandatory when a deduction is filled).
          Posting is all-or-nothing — every row must validate first.
        </div>
      </Card>

      {result && (
        <Card className="p-4 mb-4 border-emerald-300 bg-emerald-50/60" data-testid="excel-commit-result">
          <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm mb-2">
            <CheckCircle2 size={16} /> {result.voucher_count} Receipt Voucher(s) posted
          </div>
          {result.vouchers.map((v) => (
            <div key={v.voucher_number} className="text-xs text-emerald-900 py-0.5">
              <span className="font-bold">{v.voucher_number}</span> · {v.client_name} · {formatINR(v.total_amount)}
              {v.tds_total > 0 && ` (+${formatINR(v.tds_total)} TDS)`}
              {v.retention_held_total > 0 && ` · Retention held ${formatINR(v.retention_held_total)}`}
              {v.adjustments_net_delta ? ` · Deductions ${formatINR(v.adjustments_net_delta)}` : ""}
              {" · "}{v.allocation_count} invoice(s)
            </div>
          ))}
        </Card>
      )}

      {preview && (
        <Card data-testid="excel-preview">
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-xs">
              <span className="border border-emerald-200 bg-emerald-50 text-emerald-700 px-2 py-1 font-bold" data-testid="excel-valid-count">{preview.valid_count} valid</span>
              <span className={`border px-2 py-1 font-bold ${preview.error_count ? "border-rose-300 bg-rose-50 text-rose-700" : "border-slate-200 bg-slate-50 text-slate-500"}`} data-testid="excel-error-count">{preview.error_count} error(s)</span>
              <span className="border border-sky-200 bg-sky-50 text-sky-700 px-2 py-1 font-bold">{preview.voucher_groups} voucher(s) will be created</span>
            </div>
            <button data-testid="excel-commit-btn" onClick={commit}
              disabled={committing || preview.error_count > 0 || !preview.valid_count}
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-5 py-2.5 text-xs uppercase tracking-wider font-bold">
              {committing ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
              Confirm & Post
            </button>
          </div>
          <div className="overflow-x-auto max-h-[28rem] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="sticky top-0">
                <tr className="bg-slate-100 text-slate-600">
                  {["Row", "Status", "Client", "Invoice", "Date", "Amount", "TDS", "Mode", "Ref", "Outstanding", "Retention Holds", "CESS/LD/R-off", "Errors"].map((h) => (
                    <th key={h} className="px-2.5 py-2 text-left text-[10px] uppercase tracking-wider whitespace-nowrap">{h}</th>))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.row_no} className={`border-b border-slate-50 ${r.ok ? "" : "bg-rose-50/50"}`}>
                    <td className="px-2.5 py-1.5">{r.row_no}</td>
                    <td className="px-2.5 py-1.5">
                      {r.ok
                        ? <span className="flex items-center gap-1 text-emerald-600 font-bold"><CheckCircle2 size={12} /> OK</span>
                        : <span className="flex items-center gap-1 text-rose-600 font-bold"><XCircle size={12} /> Error</span>}
                    </td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.client_name}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap font-semibold">{r.invoice_number}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">{r.receipt_date}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">{formatINR(r.amount)}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">{r.tds_deducted ? formatINR(r.tds_deducted) : "—"}</td>
                    <td className="px-2.5 py-1.5">{r.mode}</td>
                    <td className="px-2.5 py-1.5">{r.reference_no || "—"}</td>
                    <td className="px-2.5 py-1.5 text-right tabular-nums">{r.outstanding != null ? formatINR(r.outstanding) : "—"}</td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">
                      {r.retention_hold_total > 0 ? (
                        <span className="border border-amber-200 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold"
                          title={Object.entries(r.retention_holds || {}).map(([h, v]) => `${h}: ${formatINR(v)}`).join(" · ")}>
                          {formatINR(r.retention_hold_total)} ({Object.keys(r.retention_holds || {}).length} head{Object.keys(r.retention_holds || {}).length > 1 ? "s" : ""})
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-2.5 py-1.5 whitespace-nowrap">
                      {r.deduction_total ? (
                        <span className="border border-rose-200 bg-rose-50 text-rose-700 px-1.5 py-0.5 text-[10px] font-bold"
                          title={[r.cess_deduction ? `CESS ${formatINR(r.cess_deduction)}` : "", r.ld_deduction ? `LD ${formatINR(r.ld_deduction)}` : "", r.roundoff_delta ? `R-off ${formatINR(r.roundoff_delta)}` : "", r.adjustment_reason ? `Reason: ${r.adjustment_reason}` : ""].filter(Boolean).join(" · ")}>
                          {formatINR(r.deduction_total)}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-2.5 py-1.5 text-rose-600">{(r.errors || []).join("; ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
